import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Lock } from "lucide-react";
import { listAdtractionTransactions, type TransactionRow } from "@/lib/adtraction.functions";
import { listLeads, type LeadRow } from "@/lib/leads.functions";

export const Route = createFileRoute("/lp/konverteringar")({
  head: () => ({
    meta: [
      { title: "Konverteringar (Adtraction) | Svensk Låneguide" },
      { name: "description", content: "Intern read-only översikt över Adtraction-konverteringar och attribution." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Konverteringar (Adtraction)" },
      { property: "og:description", content: "Intern read-only översikt över Adtraction-konverteringar." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: ConversionsPage,
});

const money = (n: number) =>
  new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 2 }).format(Math.round(n * 100) / 100) + " kr";

const dash = (v: string | null) => (v && v.trim() ? v : "–");

const kr = (n: number | null) => (n === null ? "–" : new Intl.NumberFormat("sv-SE").format(n) + " kr");
const ja = (v: boolean | null) => (v === null ? "–" : v ? "Ja" : "Nej");

/** Bästa gissning: sidkoden (epi) → sökväg på sajten. Null = ingen säker länk. */
function epiToPath(epi: string | null): string | null {
  if (!epi) return null;
  const e = epi.trim().toLowerCase();
  if (!e) return null;
  if (e === "hem" || e === "start") return "/";
  if (e.startsWith("lp-")) return "/lp/" + e.slice(3);
  if (e.startsWith("guider-")) return "/guider/" + e.slice(7);
  if (e.startsWith("nyhet-")) return "/nyhet/" + e.slice(6);
  if (e.startsWith("blogg-")) return "/blogg/" + e.slice(6);
  if (e.startsWith("service-")) return "/service/" + e.slice(8);
  if (e.startsWith("langivare-")) return "/langivare/" + e.slice(10);
  if (e.startsWith("kategori-")) return "/kategori/" + e.slice(9);
  if (e.startsWith("pseo-")) return "/" + e.slice(5);
  return "/" + e;
}

function PageCell({ epi, epi2 }: { epi: string | null; epi2: string | null }) {
  const path = epiToPath(epi);
  return (
    <div className="min-w-0">
      <div className="font-semibold text-foreground">{dash(epi)}</div>
      {path && (
        <a
          href={path}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-xs text-primary underline underline-offset-2"
        >
          {path}
        </a>
      )}
      <div className="text-xs text-muted-foreground">Placering: {dash(epi2)}</div>
    </div>
  );
}

function ConversionsPage() {
  const fetchRows = useServerFn(listAdtractionTransactions);
  const fetchLeads = useServerFn(listLeads);
  const [password, setPassword] = useState("");
  const [rows, setRows] = useState<TransactionRow[] | null>(null);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [tab, setTab] = useState<"konv" | "leads">("konv");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [channel, setChannel] = useState("all");
  const [program, setProgram] = useState("all");

  const load = async (pw: string) => {
    setLoading(true);
    setError("");
    const [res, leadRes] = await Promise.all([
      fetchRows({ data: { password: pw } }),
      fetchLeads({ data: { password: pw } }),
    ]);
    setLoading(false);
    if (!res.ok) {
      setError("Fel lösenord eller inga uppgifter kunde hämtas.");
      return;
    }
    setRows(res.rows);
    setLeads(leadRes.ok ? leadRes.leads : []);
  };


  const programs = useMemo(() => {
    const s = new Set<string>();
    (rows ?? []).forEach((r) => s.add(r.programname || r.programid || "–"));
    return [...s].sort((a, b) => a.localeCompare(b, "sv"));
  }, [rows]);

  const filtered = useMemo(() => {
    return (rows ?? []).filter((r) => {
      const d = r.actiondate ? new Date(r.actiondate) : null;
      if (from) {
        if (!d || d < new Date(from + "T00:00:00")) return false;
      }
      if (to) {
        if (!d || d > new Date(to + "T23:59:59")) return false;
      }
      const ch = (r.attribution_channel || "other").toLowerCase();
      if (channel === "meta" && ch !== "meta") return false;
      if (channel === "seo" && ch !== "seo") return false;
      if (channel === "other" && (ch === "meta" || ch === "seo")) return false;
      if (program !== "all" && (r.programname || r.programid || "–") !== program) return false;
      return true;
    });
  }, [rows, from, to, channel, program]);

  const totalCommission = filtered.reduce((s, r) => s + (r.commissiondecimal ?? 0), 0);

  const byChannel = useMemo(() => {
    const buckets: Record<string, { count: number; commission: number }> = {
      meta: { count: 0, commission: 0 },
      seo: { count: 0, commission: 0 },
      övriga: { count: 0, commission: 0 },
    };
    filtered.forEach((r) => {
      const ch = (r.attribution_channel || "other").toLowerCase();
      const key = ch === "meta" ? "meta" : ch === "seo" ? "seo" : "övriga";
      buckets[key].count += 1;
      buckets[key].commission += r.commissiondecimal ?? 0;
    });
    return buckets;
  }, [filtered]);

  const when = (r: TransactionRow) => new Date(r.actiondate ?? r.created_at).getTime();

  const sortedRows = useMemo(() => [...filtered].sort((a, b) => when(b) - when(a)), [filtered]);

  const breakdown = useMemo(() => {
    const map = new Map<
      string,
      {
        channel: string;
        source: string;
        medium: string;
        epiRaw: string | null;
        epi2Raw: string | null;
        bank: string;
        count: number;
        commission: number;
        lastAt: number;
      }
    >();
    filtered.forEach((r) => {
      const channelKey = r.attribution_channel || "other";
      const source = dash(r.attribution_source);
      const medium = dash(r.attribution_medium);
      const epi = dash(r.epi);
      const epi2 = dash(r.epi2);
      const bank = r.programname || r.programid || "–";
      const key = [channelKey, source, medium, epi, epi2, bank].join("|");
      const cur =
        map.get(key) ??
        {
          channel: channelKey,
          source,
          medium,
          epiRaw: r.epi,
          epi2Raw: r.epi2,
          bank,
          count: 0,
          commission: 0,
          lastAt: 0,
        };
      cur.count += 1;
      cur.commission += r.commissiondecimal ?? 0;
      cur.lastAt = Math.max(cur.lastAt, when(r));
      map.set(key, cur);
    });
    return [...map.values()].sort((a, b) => b.lastAt - a.lastAt);
  }, [filtered]);


  if (!rows) {
    return (
      <main className="grid min-h-screen place-items-center bg-background px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load(password);
          }}
          className="w-full max-w-sm rounded-md border border-border bg-card p-6 shadow-elevated"
        >
          <div className="flex items-center gap-2 font-display text-xl font-extrabold text-foreground">
            <Lock className="h-5 w-5 text-primary" /> Konverteringar
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Ange lösenord för att se Adtraction-statistik.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Lösenord"
            className="mt-4 w-full rounded-md border border-input bg-card px-4 py-3 text-[15px] outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
          {error && <p className="mt-2 text-sm font-semibold text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-4 w-full rounded-md bg-cta py-3 text-base font-bold text-cta-foreground disabled:opacity-50"
          >
            {loading ? "Hämtar…" : "Logga in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
          <h1 className="truncate font-display text-2xl font-extrabold text-foreground sm:text-3xl">
            Konverteringar (Adtraction)
          </h1>
          <button
            onClick={() => void load(password)}
            className="shrink-0 rounded-md border border-border bg-card px-4 py-2 text-sm font-semibold hover:bg-muted"
          >
            Uppdatera
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Read-only. Testtransaktioner (uniqueid som börjar med TEST_) räknas aldrig.
        </p>

        {/* Flikar */}
        <div className="mt-6 flex gap-2">
          {([
            ["konv", `Konverteringar (${rows.length})`],
            ["leads", `Leads (${leads.length})`],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={
                "rounded-md border px-4 py-2 text-sm font-semibold " +
                (tab === key
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:bg-muted")
              }
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" ? (
          <LeadsTab leads={leads} />
        ) : (
        <>
        {/* Filter */}
        <div className="mt-6 grid gap-3 rounded-md border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">

          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Från (actiondate)</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-md border border-input bg-card px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Till (actiondate)</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded-md border border-input bg-card px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Kanal</span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full rounded-md border border-input bg-card px-3 py-2"
            >
              <option value="all">Alla kanaler</option>
              <option value="meta">Meta</option>
              <option value="seo">SEO</option>
              <option value="other">Övriga</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Program/annonsör</span>
            <select
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              className="w-full rounded-md border border-input bg-card px-3 py-2"
            >
              <option value="all">Alla program</option>
              {programs.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* KPI */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Kpi label="Faktiska konverteringar" value={new Intl.NumberFormat("sv-SE").format(filtered.length)} />
          <Kpi label="Total provision" value={money(totalCommission)} />
        </div>

        {/* Per kanal */}
        <h2 className="mt-8 font-display text-xl font-extrabold text-foreground">Per kanal</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          {(["meta", "seo", "övriga"] as const).map((k) => (
            <div key={k} className="rounded-md border border-border bg-card p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
              <div className="mt-1 font-display text-2xl font-extrabold text-foreground">{byChannel[k].count}</div>
              <div className="text-sm text-muted-foreground">{money(byChannel[k].commission)}</div>
            </div>
          ))}
        </div>

        {/* Senaste konverteringar */}
        <h2 className="mt-8 font-display text-xl font-extrabold text-foreground">Senaste konverteringar</h2>
        {sortedRows.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Inga konverteringar matchar filtret.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-md border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {["Datum", "Sida", "Bank", "Kanal", "Källa", "Ordervärde", "Provision", "Status"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.uniqueid} className="border-t border-border/60 align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      {new Date(r.actiondate ?? r.created_at).toLocaleString("sv-SE")}
                    </td>
                    <td className="px-3 py-2">
                      <PageCell epi={r.epi} epi2={r.epi2} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-semibold">{r.programname || r.programid || "–"}</td>
                    <td className="whitespace-nowrap px-3 py-2">{dash(r.attribution_channel)}</td>
                    <td className="whitespace-nowrap px-3 py-2">{dash(r.attribution_source)}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {r.ordervaluedecimal === null ? "–" : money(r.ordervaluedecimal)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{money(r.commissiondecimal ?? 0)}</td>
                    <td className="whitespace-nowrap px-3 py-2">{dash(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sammanställning */}
        <h2 className="mt-8 font-display text-xl font-extrabold text-foreground">Sammanställning</h2>
        {breakdown.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Inga konverteringar matchar filtret.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-md border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {["Senaste", "Sida", "Bank", "Kanal", "Källa", "Medium", "Konverteringar", "Provision"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b) => (
                  <tr
                    key={[b.channel, b.source, b.medium, b.epiRaw, b.epi2Raw, b.bank].join("|")}
                    className="border-t border-border/60 align-top"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      {b.lastAt ? new Date(b.lastAt).toLocaleString("sv-SE") : "–"}
                    </td>
                    <td className="px-3 py-2">
                      <PageCell epi={b.epiRaw} epi2={b.epi2Raw} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-semibold">{b.bank}</td>
                    <td className="whitespace-nowrap px-3 py-2">{b.channel}</td>
                    <td className="px-3 py-2">{b.source}</td>
                    <td className="px-3 py-2">{b.medium}</td>
                    <td className="whitespace-nowrap px-3 py-2">{b.count}</td>
                    <td className="whitespace-nowrap px-3 py-2">{money(b.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>
    </main>
  );
}

function LeadsTab({ leads }: { leads: LeadRow[] }) {
  if (leads.length === 0) {
    return <p className="mt-6 text-sm text-muted-foreground">Inga formulär inskickade ännu.</p>;
  }
  return (
    <div className="mt-6 overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {["Datum", "Namn", "E-post", "Mobil", "Belopp", "Inkomst", "Mål", "Skuldtyper", "KFM", "Anm.", "UC", "Förslag", "Källa"].map(
              (h) => (
                <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-border/60 align-top">
              <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                {new Date(l.created_at).toLocaleString("sv-SE")}
              </td>
              <td className="px-3 py-2 font-semibold">{l.name}</td>
              <td className="px-3 py-2">{l.email}</td>
              <td className="whitespace-nowrap px-3 py-2">{l.phone}</td>
              <td className="whitespace-nowrap px-3 py-2">{kr(l.amount)}</td>
              <td className="whitespace-nowrap px-3 py-2">{kr(l.income)}</td>
              <td className="px-3 py-2">{l.goal ?? "–"}</td>
              <td className="px-3 py-2">{(l.debt_types ?? []).join(", ") || "–"}</td>
              <td className="px-3 py-2">{ja(l.kfm)}</td>
              <td className="px-3 py-2">{ja(l.remarks)}</td>
              <td className="px-3 py-2">{l.uc_count ?? "–"}</td>
              <td className="px-3 py-2">{(l.matched_lenders ?? []).join(", ") || "–"}</td>
              <td className="px-3 py-2">{l.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-3xl font-extrabold text-foreground">{value}</div>
    </div>
  );
}
